import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
// import { DeviceMapVO } from 'src/app/services/device-mapping/device-mapping.service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, mergeAll, take } from 'rxjs/operators';
import { PaginationParam } from 'src/app/clinical/dashboards/patient-list/patient-list.model';
import { ReturnDeviceFormData } from 'src/app/patient/devices/dialogs/return-device-dialog/return-device-dialog.component';
import { OrderVO, SearchUnmappedDevices } from 'src/app/services/device-ordering/value-objects/order.vo';
import { FirestoreService } from 'src/app/services/firestore.service';
import { environment } from 'src/environments/environment';
import { NewAuthService } from '../auth/new-auth-service.service';
import { DeviceVO, PatientVO } from '../device-ordering/value-objects/order.vo';
import { UtilsService } from '../utils.service';
import { InitialDataModel } from './../models/initial-data-model.service';
import { ActiveDeviceStatus, UnmapDeviceRequest } from './models/device.model';
import { DeviceUserMapping } from '../../services/device-mapping/models/device.model';
import { OrderRequestVO } from '../../services/device-ordering/value-objects/order.vo';

export interface DeviceMapVO {
  id: string;
  owner: string;
  device: DeviceVO;
  patient: PatientVO;
  createdAt: number;
  status?: ActiveDeviceStatus;
  order?: OrderVO;
  doc?: firebase.firestore.DocumentSnapshot;
  updatedAt: number | firebase.firestore.Timestamp | Date | string;
  unmappedReason?: string;
  isReturning?: boolean;
}
export interface ReturningDeviceVO extends DeviceMapVO {
  state: string;
  toggleForm: UntypedFormControl;
  unmappedReason: string;
}

@Injectable({
  providedIn: 'root',
})
export class DeviceMappingService {
  maxClientAccounts = 15;
  constructor(
    private http: HttpClient,
    private fsService: FirestoreService,
    private auth: NewAuthService,
    private initialModelService: InitialDataModel,
    private utilsService: UtilsService
  ) {}

  getUnmappedDevices(uid?: string): Observable<any[]> {
    const availableClients = this.initialModelService.clientAccounts;
    if (this.auth.user.roles.isAdmin || this.auth.user.roles.isWelbyClinical) {
      return this.fsService.col$('hardware_not_mapped');
    } else if (availableClients.length <= this.maxClientAccounts) {
      return this.fsService.col$('hardware_not_mapped', (ref) => ref.where('client_id', 'in', availableClients));
    } else {
      const chunks$ = this.utilsService.getChunksObsWhenArrayLengthIsGreaterThanMaxValue(availableClients);

      return chunks$.pipe(
        map((chunk) => this.fsService.col$('hardware_not_mapped', (ref) => ref.where('client_id', 'in', chunk))),
        mergeAll()
      );
    }
  }

  unmappedDeviceSearch(pageSize: number, paginationParams?: PaginationParam): Observable<SearchUnmappedDevices> {
    return this.http.post<SearchUnmappedDevices>(`${environment.welbyEndpoint}/api/v1/device-utils/unmapped/search`, {
      pageSize,
      page: paginationParams.page,
    });
  }

  getUnmappedDevicesByImei(imei: string, manufacturer?: string): Observable<any[]> {
    const availableClients = this.initialModelService.clientAccounts;
    if (this.auth.user.roles.isAdmin || this.auth.user.roles.isWelbyClinical) {
      if (manufacturer) {
        return this.fsService.col$('hardware_not_mapped', (ref) =>
          ref.where('device.imei', '>=', imei).where('device.manufacturer', '==', manufacturer)
        );
      } else {
        return this.fsService.col$('hardware_not_mapped', (ref) =>
          ref.where('device.imei', '>=', imei).where('device.imei', '<', imei + '\uf8ff')
        );
      }
    } else if (availableClients.length <= this.maxClientAccounts) {
      return manufacturer
        ? this.fsService.col$('hardware_not_mapped', (ref) =>
            ref
              .where('client_id', 'in', availableClients)
              .where('device.imei', '>=', imei)
              .where('device.manufacturer', '==', manufacturer)
          )
        : this.fsService.col$('hardware_not_mapped', (ref) =>
            ref.where('client_id', 'in', availableClients).where('device.imei', '>=', imei)
          );
    } else {
      const chunks$ = this.utilsService.getChunksObsWhenArrayLengthIsGreaterThanMaxValue(availableClients);

      return chunks$.pipe(
        map((chunk) =>
          this.fsService.col$('hardware_not_mapped', (ref) =>
            ref.where('client_id', 'in', chunk).where('device.imei', '>=', imei)
          )
        ),
        mergeAll()
      );
    }
  }

  getMappedDevicesByUserAndPatient(userId: string, patientId: string): Observable<any> {
    return this.http.get(environment.welbyEndpoint + `/api/v1/device/mapping/user/${userId}/patient/${patientId}`);
  }

  mapPatientToInventoryDevice(
    device: DeviceVO,
    patient: PatientVO,
    comments?: string,
    trackingInfo?: any
  ): Observable<any> {
    return this.http.post(environment.welbyEndpoint + `/api/v1/device/mapping/map`, {
      patient,
      device,
      comments: comments ?? '',
      trackingInfo,
    });
  }

  unmapDevice(device: DeviceVO, reason: string, readyToBeRefurbished = true, justUnmap = false): Observable<void> {
    return this.http.post<void>(environment.welbyEndpoint + `/api/v1/device/mapping/unmap`, {
      device,
      unmap_reason: reason,
      readyToBeRefurbished,
      justUnmap,
    });
  }

  unmapRequestDevice(device: DeviceVO, reason: string): Observable<UnmapDeviceRequest> {
    return this.http.post<UnmapDeviceRequest>(environment.welbyEndpoint + `/api/v1/device/mapping/unmap-request`, {
      device,
      unmap_reason: reason,
    });
  }

  unlinkDevice(imei: string, patientId: string, manufacturer: string) {
    return this.http.post(environment.welbyEndpoint + `/api/v1/device/unlink`, {
      imei,
      patientId,
      vendorId: manufacturer,
    });
  }

  getDevicesByPatient(patientId: string): Observable<DeviceMapVO[]> {
    return this.http.get<DeviceMapVO[]>(environment.welbyEndpoint + `/api/v1/device/mapping/${patientId}`);
  }

  getDeviceUserMapping(id: string): Observable<DeviceUserMapping> {
    return this.fsService
      .doc(`device_user_mapping/${id}`)
      .get()
      .pipe(
        take(1),
        map((res) => res.data())
      );
  }

  getOrderRequest(patientId: string, sku_display: string): Observable<OrderRequestVO[]> {
    return this.fsService
      .col<OrderRequestVO>(`hardware_order_requests`, (ref) =>
        ref
          .where('approved', '==', 'true')
          .where('request_body.patient.id', '==', patientId)
          .where('request_body.device.sku_display', '==', sku_display)
      )
      .get()
      .pipe(
        map((res) => res.docs.map((doc) => doc.data() as OrderRequestVO)),
        take(1)
      );
  }

  updateAutoRefilInDeviceUserMapping(id: string, auto_refill: boolean) {
    return this.fsService.update(`device_user_mapping/${id}`, { auto_refill });
  }

  suspendDevice(device: DeviceVO): Observable<any> {
    return this.http.post(
      environment.welbyEndpoint + `/api/v1/device-utils/suspend/${device.manufacturer}/${device.imei}`,
      {}
    );
  }

  activateDevice(device: DeviceVO): Observable<any> {
    return this.http.post(
      environment.welbyEndpoint + `/api/v1/device-utils/activate/${device.manufacturer}/${device.imei}`,
      {}
    );
  }

  getDeviceStatus(imei: string, manufacturer: string): Observable<ActiveDeviceStatus> {
    return this.http.get<ActiveDeviceStatus>(
      environment.welbyEndpoint + `/api/v1/device-utils/status/${manufacturer}/${imei}`
    );
  }

  getUnmapReasons(): Observable<string[]> {
    return this.fsService
      .doc<{ reasons: string[] }>('welby_configuration/unmap_reasons')
      .valueChanges()
      .pipe(map((data) => data?.reasons || []));
  }

  getDevicesByPatientId(patientId: string): Observable<any> {
    return this.fsService.colWithIds$('hardware_user_mappings', (ref) => ref.where('patient.id', '==', patientId));
  }

  returnDeviceRequest(data: ReturnDeviceFormData): Observable<any> {
    return this.http.post(environment.welbyEndpoint + `/api/v1/device/mapping/unmap-request/bulk`, data);
  }
}
